/**
 * Navigation- und Store-Konfiguration der App.
 *
 * Routen: top-level domänenbasiert (öffentliche Listen-/Detailrouten der
 * Fach-Entitäten — echte Routen, keine Anker: teilbare Links, Verlauf, SEO),
 * plus members/<feature> für die Personalisierung (auth-protected).
 * Login: kein eigener Page — nur `includeLogin: true` aktiviert das in
 * pd-spa-helper eingebaute Login-Modal.
 */

import { msg } from "@lit/localize";
import type {
  NavigationConfig,
  StoreConfig,
  MenuElement,
} from "@progressive-development/pd-spa-helper";
import { appUser } from "@progressive-development/politik-model";

import { defaultEffects, defaultReducer } from "../common/store/store-exports.js";

// =============================================================================
// Route-Konstanten
// =============================================================================

export const ROUTE_HOME = "home";
export const ROUTE_ABOUT = "about";
/** Als App installieren — eigene Adresse, weil sie verschickt wird. */
export const ROUTE_APP = "app";
export const ROUTE_IMPRINT = "imprint";
export const ROUTE_PRIVACY = "privacy";

// Öffentliche Listen-/Detailrouten der Fach-Entitäten (Abschnitt 2).
export const ROUTE_SITZUNGEN = "sitzungen";
export const ROUTE_SITZUNG_DETAIL = "sitzung/detail";
/**
 * Ziel-URLs aus dem Sitemap-Entwurf v2 (E1): Die Liste heißt `/entscheidungen`,
 * das Detail trägt die amtliche Referenz als sprechenden Slug
 * (`/entscheidungen/an-0673-2026`) — zitierfähig und wiederfindbar. Gebildet
 * wird der Slug in der Pipeline, die App liest ihn nur.
 */
export const ROUTE_ENTSCHEIDUNGEN = "entscheidungen";
/**
 * Kapitel 3 — die Übersicht (`/wahlen`, `/wahlen/:wahl`) und die Detailseite
 * mit den drei Achsen in der Adresse (Fachkonzept 2.2). Die Adressen bildet
 * `modules/wahlen/wahl-auswahl.ts` (`wahlPfad`, `auswahlAusRoute`) — die eine
 * Stelle, an der die Form steht; hier nur die Muster.
 */
export const ROUTE_WAHLEN = "wahlen";
export const ROUTE_WAHL_DETAIL = "wahl/detail";
export const ROUTE_DRUCKSACHE_DETAIL = "drucksache/detail";
export const ROUTE_GREMIEN = "gremien";
/**
 * Die vier Übersichten von Kapitel 2 — je ein Ausschnitt statt einer
 * Sammelseite: Jede Kachel der Startseite führt in ihren eigenen.
 */
export const ROUTE_BEZIRKSVERTRETUNGEN = "bezirksvertretungen";
export const ROUTE_AUSSCHUESSE = "ausschuesse";
/** Der Rat — eine Seite, nicht ein Eintrag der Gremienliste (FACHKONZEPT-rat.md 2.1). */
export const ROUTE_RAT = "rat";
/** Parteien: Übersicht und eine Seite je kuratiertem Slug (E1). */
export const ROUTE_PARTEIEN = "parteien";
export const ROUTE_PARTEI_DETAIL = "parteien/detail";
export const ROUTE_DEZERNATE = "dezernate";
export const ROUTE_GREMIUM_DETAIL = "gremium/detail";

/**
 * Der Weg auf ein Gremium.
 *
 * Eigene Funktion, weil der Routen-NAME ("gremium/detail") und ihr MUSTER
 * ("/gremium/:slug") verschieden sind — aus dem Namen lässt sich der Pfad also
 * nicht bilden. Ohne sie stünde die Zeichenkette an jeder Aufrufstelle, und
 * beim nächsten Zuschnitt der Adressen fände man nicht alle.
 */
export const gremiumPfad = (slug: string): string => `/gremium/${slug}`;
/** Der Weg auf eine Parteiseite — die eine Stelle, die den Pfad kennt. */
export const parteiPfad = (slug: string): string => `/${ROUTE_PARTEIEN}/${slug}`;

/**
 * Der Weg auf eine Sitzung.
 *
 * Das Muster ist `/sitzungen/:id` — dieselbe Wurzel wie die Liste, nicht
 * `/sitzung/:id`. Aus dem Routen-Namen allein ist das nicht abzulesen, deshalb
 * steht der Pfad hier statt an jeder Aufrufstelle.
 */
export const sitzungPfad = (id: string): string => `/${ROUTE_SITZUNGEN}/${id}`;

/**
 * Der Weg auf eine Drucksache.
 *
 * Das Muster ist `/entscheidungen/:referenz` — die Wurzel der LISTE, nicht
 * `/drucksache/:referenz`. Dieselbe Falle wie bei der Sitzung: Der Routen-NAME
 * lautet `drucksache/detail` und verfuehrt dazu, ihn fuer den Pfad zu halten.
 */
export const drucksachePfad = (referenz: string): string => `/${ROUTE_ENTSCHEIDUNGEN}/${referenz}`;

/** Der Weg auf die Bezirksvertretung eines Bezirks — über dessen Slug. */
export const bezirksvertretungPfad = (bezirkSlug: string): string =>
  `/${ROUTE_BEZIRKSVERTRETUNG}/${bezirkSlug}`;
export const ROUTE_PERSON_DETAIL = "person/detail";
/**
 * Die zwei Gebietsebenen — GETRENNTE Adressräume, und das ist zwingend.
 *
 * 8 der 9 Bezirks-Slugs sind gleichzeitig Stadtteil-Slugs (ehrenfeld, nippes,
 * kalk, porz, chorweiler, rodenkirchen, lindenthal, muelheim). Ein gemeinsamer
 * Pfad wäre also mehrdeutig, sobald es beide Seiten gibt.
 *
 * Und die Begriffe sitzen so richtig herum: In Köln ist das VEEDEL der
 * Stadtteil, nicht der Bezirk. Die kleinere Seite hat den besseren Anspruch
 * auf das Wort — die größere heißt, was sie ist.
 *
 * Nicht `/veedel/:bezirk/:stadtteil`: Das erzwänge den Bezirk in der Adresse,
 * und ein falscher Bezirk darin wäre eine Lüge in der URL. Stadtteil-Slugs
 * sind untereinander eindeutig, ein Segment genügt.
 */
/** Die Übersicht der Gebiete — der Eingang der Ort-Achse im Hauptmenü. */
export const ROUTE_BEZIRKE = "bezirke";
export const ROUTE_BEZIRK = "bezirk";
/**
 * Die Bezirksvertretung eines Bezirks — adressiert über den BEZIRKS-Slug.
 *
 * Nicht über die Kennung der Quelle (`oparl-koeln-b822f…`): Jede der neun
 * gehört zu genau einem Bezirk, der Slug ist stabil und sprechend, und eine
 * undurchsichtige Kennung in der Adresse widerspräche E1.
 *
 * Eigene Route statt einer allgemeinen Gremien-Seite: Die Bezirksvertretung
 * bekommt zuerst eine, die ihr passt. Was sich mit Rat und Ausschüssen
 * wirklich teilt, zeigt sich, wenn die dran sind — vorher wäre es geraten.
 */
export const ROUTE_BEZIRKSVERTRETUNG = "bezirksvertretung";
export const ROUTE_VEEDEL = "veedel";
// Rahmen (FACHKONZEPT-rahmen.md): die zwei Seiten, die dem Fuß fehlen.
export const ROUTE_QUELLEN = "quellen";
export const ROUTE_SITEMAP = "sitemap";

// Members: Personalisierung (Abschnitt 3) — Dashboard ist Landing nach Login.
export const ROUTE_MEMBERS_DASHBOARD = "members/dashboard";
export const ROUTE_MEMBERS_PROFILE = "members/profile";
export const ROUTE_MEMBERS_MERKLISTE = "members/merkliste";
export const ROUTE_MEMBERS_MERKLISTE_DETAIL = "members/merkliste/detail";
// Admin: Sync-Status (Abschnitt 4).
export const ROUTE_MEMBERS_SYNC = "members/sync";

// TEMPLATE-DOC: Routen der In-App-Doku — Block komplett löschbar.
export const ROUTE_DOC_AUFBAU = "doc/aufbau";
export const ROUTE_DOC_PROVIDER = "doc/provider";
export const ROUTE_DOC_PATTERNS = "doc/patterns";
export const ROUTE_DOC_ENTFERNEN = "doc/entfernen";

// =============================================================================
// Section-IDs (Startseiten-One-Pager, v2: die vier Themenblöcke)
//
// Einstieg (Block 0, Suche + Gebietsraster) und Quellen & Stand haben KEINEN
// Menüeintrag — das Menü sind die vier Blöcke. Zurück zum Einstieg führt der
// Logo-Klick (menuLogoRoute).
// =============================================================================

export const SEC_EINSTIEG = "secEinstieg";
export const SEC_WAHLEN = "secWahlen";
export const SEC_ENTSCHEIDUNGEN = "secEntscheidungen";
export const SEC_GREMIEN = "secGremien";
export const SEC_TERMINE = "secTermine";
export const SEC_QUELLEN = "secQuellen";

/** Logo-Klick navigiert zur Startseite (scrollt nach oben, zum Einstieg zurück). */
const LOGO_ROUTE = `/${ROUTE_HOME}`;

// =============================================================================
// Menu-Definitionen (factories — werden beim Locale-Wechsel neu aufgerufen)
// =============================================================================

/**
 * DAS Hauptmenü — überall identisch, immer Seitenlinks (KONZEPT-navigation.md).
 *
 * Vier Themen, vier Seiten. Keine `sec`-Einträge: Ein Menüpunkt, der auf
 * einer Seite scrollt und auf der anderen navigiert, ist genau die
 * Verwechslung, die ein Hauptmenü nicht haben darf. Wo eine Seite lang ist,
 * hat sie ihre eigene Abschnittsleiste (`app-abschnitte`).
 *
 * Menü = Thema, Seite = Begriff: Hier steht „Entscheidungen" (die Frage, mit
 * der jemand kommt), auf der Seite dann „Drucksache" (das amtliche Objekt).
 */
export const hauptMenuItems = (): MenuElement[] => [
  // Der Ort zuerst — wie die Startseite: die zweite Achse des ganzen Portals.
  { name: msg("Bezirke", { id: "app.menu.bezirke" }), key: "bezirke", route: `/${ROUTE_BEZIRKE}` },
  { name: msg("Wahlen", { id: "app.menu.wahlen" }), key: "wahlen", route: `/${ROUTE_WAHLEN}` },
  {
    name: msg("Entscheidungen", { id: "app.menu.entscheidungen" }),
    key: "entscheidungen",
    route: `/${ROUTE_ENTSCHEIDUNGEN}`,
  },
  { name: msg("Gremien", { id: "app.menu.gremien" }), key: "gremien", route: `/${ROUTE_GREMIEN}` },
  {
    name: msg("Termine", { id: "app.menu.termine" }),
    key: "termine",
    route: `/${ROUTE_SITZUNGEN}`,
  },
];

// TEMPLATE-DOC: eigenes Seitenmenü der Doku (statisch deutsch, ohne msg()).
const docMenuItems = (): MenuElement[] => [
  { name: "Aufbau & Architektur", key: "docAufbau", route: `/${ROUTE_DOC_AUFBAU}` },
  { name: "Provider (Mock/Firebase)", key: "docProvider", route: `/${ROUTE_DOC_PROVIDER}` },
  { name: "Patterns", key: "docPatterns", route: `/${ROUTE_DOC_PATTERNS}` },
  { name: "Lösch-Guide", key: "docEntfernen", route: `/${ROUTE_DOC_ENTFERNEN}` },
];

const membersMenuItems = (): MenuElement[] => [
  {
    name: msg("Mein Bereich", { id: "app.menu.dashboard" }),
    key: "dashboard",
    route: `/${ROUTE_MEMBERS_DASHBOARD}`,
  },
  {
    name: msg("Merkliste", { id: "app.bookmarks.title" }),
    key: "merkliste",
    route: `/${ROUTE_MEMBERS_MERKLISTE}`,
  },
  {
    name: msg("Profil", { id: "app.menu.profile" }),
    key: "profile",
    route: `/${ROUTE_MEMBERS_PROFILE}`,
  },
  // requiredRoles steuert die SICHTBARKEIT des Menüpunkts;
  // den ZUGRIFF sichert zusätzlich `roles` an der Route (unten).
  {
    name: msg("Sync-Status", { id: "app.sync.title" }),
    key: "sync",
    route: `/${ROUTE_MEMBERS_SYNC}`,
    requiredRoles: [appUser.AppUserRole.ADMIN],
  },
];

// Keine Top-Leiste (FACHKONZEPT-rahmen.md, 1): Die Kopfleiste besteht aus Logo
// und Hauptmenü. Quellen wohnen im Fuß, Persönliches kommt mit dem Login als
// Menü-Link zurück, die Doku ist Entwicklerwerkzeug. pd-site-header blendet die
// Leiste bei leerer Liste selbst aus — hier steht deshalb schlicht nichts.

// =============================================================================
// Navigation Config
// =============================================================================

/** Gemeinsame Defaults der öffentlichen Fach-Seiten. */
const publicPage = {
  auth: false,
  menuLogoRoute: LOGO_ROUTE,
  withFooter: true,
};

export const navigationConfig: NavigationConfig = {
  pages: [
    {
      name: ROUTE_HOME,
      pattern: ["", `/${ROUTE_HOME}`],
      auth: false,
      menuLogoRoute: LOGO_ROUTE,
      menu: hauptMenuItems(),
      // Kein Teaser: Die Seite beginnt mit Suche und Gebietseinstieg —
      // kein Hero, keine Bühne (visuelles Konzept, Block 0).
      withTeaser: false,
      withFooter: true,
    },
    {
      name: ROUTE_ABOUT,
      pattern: [`/${ROUTE_ABOUT}`],
      ...publicPage,
      menu: hauptMenuItems(),
    },
    { name: ROUTE_APP, pattern: [`/${ROUTE_APP}`], ...publicPage, menu: hauptMenuItems() },
    { name: ROUTE_IMPRINT, pattern: [`/${ROUTE_IMPRINT}`], ...publicPage, menu: hauptMenuItems() },
    { name: ROUTE_PRIVACY, pattern: [`/${ROUTE_PRIVACY}`], ...publicPage, menu: hauptMenuItems() },

    // Öffentliche Fach-Routen: echte Listen-/Detailrouten (Abschnitt 2 "Wichtig").
    {
      name: ROUTE_SITZUNGEN,
      pattern: [`/${ROUTE_SITZUNGEN}`],
      ...publicPage,
      menu: hauptMenuItems(),
    },
    {
      name: ROUTE_SITZUNG_DETAIL,
      pattern: [`/${ROUTE_SITZUNGEN}/:id`],
      ...publicPage,
      menu: hauptMenuItems(),
    },
    {
      name: ROUTE_ENTSCHEIDUNGEN,
      pattern: [`/${ROUTE_ENTSCHEIDUNGEN}`],
      ...publicPage,
      menu: hauptMenuItems(),
    },
    {
      name: ROUTE_DRUCKSACHE_DETAIL,
      pattern: [`/${ROUTE_ENTSCHEIDUNGEN}/:referenz`],
      ...publicPage,
      menu: hauptMenuItems(),
    },
    {
      // Die Übersicht — mit Wahl-Slug das Jahr aufgeklappt (Fachkonzept 3.4).
      name: ROUTE_WAHLEN,
      pattern: [`/${ROUTE_WAHLEN}`, `/${ROUTE_WAHLEN}/:wahl`],
      ...publicPage,
      menu: hauptMenuItems(),
    },
    {
      // Die Detailseite: Wahl und Art Pflicht, der Ort ein Anhang. Bei der
      // Bezirksvertretung steht der Bezirk direkt hinter der Art — er gehört
      // zur Teilwahl, nicht zum Ort; diese Muster liefern keinen art-Parameter.
      name: ROUTE_WAHL_DETAIL,
      pattern: [
        `/${ROUTE_WAHLEN}/:wahl/bezirksvertretung/:bezirk/veedel/:stadtteil`,
        `/${ROUTE_WAHLEN}/:wahl/bezirksvertretung/:bezirk`,
        `/${ROUTE_WAHLEN}/:wahl/:art/bezirk/:bezirk`,
        `/${ROUTE_WAHLEN}/:wahl/:art/veedel/:stadtteil`,
        `/${ROUTE_WAHLEN}/:wahl/:art`,
      ],
      ...publicPage,
      menu: hauptMenuItems(),
    },
    {
      name: ROUTE_BEZIRKSVERTRETUNGEN,
      pattern: [`/${ROUTE_BEZIRKSVERTRETUNGEN}`],
      ...publicPage,
      menu: hauptMenuItems(),
    },
    {
      name: ROUTE_AUSSCHUESSE,
      pattern: [`/${ROUTE_AUSSCHUESSE}`],
      ...publicPage,
      menu: hauptMenuItems(),
    },
    {
      name: ROUTE_PARTEIEN,
      pattern: [`/${ROUTE_PARTEIEN}`],
      ...publicPage,
      menu: hauptMenuItems(),
    },
    {
      name: ROUTE_PARTEI_DETAIL,
      pattern: [`/${ROUTE_PARTEIEN}/:slug`],
      ...publicPage,
      menu: hauptMenuItems(),
    },
    {
      name: ROUTE_RAT,
      pattern: [`/${ROUTE_RAT}`],
      ...publicPage,
      menu: hauptMenuItems(),
    },
    {
      name: ROUTE_DEZERNATE,
      pattern: [`/${ROUTE_DEZERNATE}`],
      ...publicPage,
      menu: hauptMenuItems(),
    },
    {
      name: ROUTE_GREMIEN,
      pattern: [`/${ROUTE_GREMIEN}`],
      ...publicPage,
      menu: hauptMenuItems(),
    },
    {
      name: ROUTE_GREMIUM_DETAIL,
      pattern: ["/gremium/:slug"],
      ...publicPage,
      menu: hauptMenuItems(),
    },
    {
      name: ROUTE_PERSON_DETAIL,
      pattern: ["/person/:id"],
      ...publicPage,
      menu: hauptMenuItems(),
    },
    {
      name: ROUTE_BEZIRKSVERTRETUNG,
      pattern: [`/${ROUTE_BEZIRKSVERTRETUNG}/:bezirk`],
      ...publicPage,
      menu: hauptMenuItems(),
    },
    {
      name: ROUTE_BEZIRKE,
      pattern: [`/${ROUTE_BEZIRKE}`],
      ...publicPage,
      menu: hauptMenuItems(),
    },
    {
      name: ROUTE_BEZIRK,
      pattern: [`/${ROUTE_BEZIRK}/:bezirk`, `/${ROUTE_BEZIRK}`],
      ...publicPage,
      menu: hauptMenuItems(),
    },
    {
      name: ROUTE_VEEDEL,
      pattern: [`/${ROUTE_VEEDEL}/:stadtteil`],
      ...publicPage,
      menu: hauptMenuItems(),
    },
    { name: ROUTE_QUELLEN, pattern: [`/${ROUTE_QUELLEN}`], ...publicPage, menu: hauptMenuItems() },
    { name: ROUTE_SITEMAP, pattern: [`/${ROUTE_SITEMAP}`], ...publicPage, menu: hauptMenuItems() },

    // Members: auth-protected — Dashboard ist die Landing nach Login.
    {
      name: ROUTE_MEMBERS_DASHBOARD,
      pattern: [`/${ROUTE_MEMBERS_DASHBOARD}`, "/members"],
      auth: true,
      menuLogoRoute: LOGO_ROUTE,
      menu: membersMenuItems(),
      withFooter: true,
    },
    {
      name: ROUTE_MEMBERS_PROFILE,
      pattern: [`/${ROUTE_MEMBERS_PROFILE}`],
      auth: true,
      menuLogoRoute: LOGO_ROUTE,
      menu: membersMenuItems(),
      withFooter: true,
    },
    {
      name: ROUTE_MEMBERS_MERKLISTE,
      pattern: [`/${ROUTE_MEMBERS_MERKLISTE}`],
      auth: true,
      menuLogoRoute: LOGO_ROUTE,
      menu: membersMenuItems(),
      withFooter: true,
    },
    {
      name: ROUTE_MEMBERS_MERKLISTE_DETAIL,
      pattern: [`/${ROUTE_MEMBERS_MERKLISTE}/:bookmarkId`],
      auth: true,
      menuLogoRoute: LOGO_ROUTE,
      menu: membersMenuItems(),
      withFooter: true,
    },
    {
      // roles am Routen-Eintrag = Zugriffs-Guard (OR-Logik über Claims).
      name: ROUTE_MEMBERS_SYNC,
      pattern: [`/${ROUTE_MEMBERS_SYNC}`],
      auth: true,
      menuLogoRoute: LOGO_ROUTE,
      roles: [appUser.AppUserRole.ADMIN],
      menu: membersMenuItems(),
      withFooter: true,
    },

    // TEMPLATE-DOC: Seiten der In-App-Doku — Block komplett löschbar.
    {
      name: ROUTE_DOC_AUFBAU,
      pattern: ["/doc", `/${ROUTE_DOC_AUFBAU}`],
      ...publicPage,
      menu: docMenuItems(),
    },
    {
      name: ROUTE_DOC_PROVIDER,
      pattern: [`/${ROUTE_DOC_PROVIDER}`],
      ...publicPage,
      menu: docMenuItems(),
    },
    {
      name: ROUTE_DOC_PATTERNS,
      pattern: [`/${ROUTE_DOC_PATTERNS}`],
      ...publicPage,
      menu: docMenuItems(),
    },
    {
      name: ROUTE_DOC_ENTFERNEN,
      pattern: [`/${ROUTE_DOC_ENTFERNEN}`],
      ...publicPage,
      menu: docMenuItems(),
    },
  ],
  includeLogin: true,
};

// =============================================================================
// Store Config
// =============================================================================

export const storeConfig: StoreConfig = {
  appName: "PolitikPortalApp",
  reducer: defaultReducer as never,
  effects: defaultEffects,
};

// =============================================================================
// Locale-Change-Handler
// =============================================================================

/**
 * Beim Locale-Wechsel müssen alle msg()-basierten Menu-Items neu erzeugt werden,
 * damit Lit die Strings aktualisiert. Die `ref`-Werte (gesetzt von
 * `_handleInitMenuSections` für Scroll-Targets) müssen aber erhalten bleiben.
 * (Aktuell nur de — der Listener bleibt fürs Muster und für spätere Locales.)
 */
const HAUPT_MENU_ROUTES = [
  ROUTE_HOME,
  ROUTE_ABOUT,
  ROUTE_APP,
  ROUTE_IMPRINT,
  ROUTE_PRIVACY,
  ROUTE_SITZUNGEN,
  ROUTE_SITZUNG_DETAIL,
  ROUTE_ENTSCHEIDUNGEN,
  ROUTE_DRUCKSACHE_DETAIL,
  ROUTE_WAHLEN,
  ROUTE_WAHL_DETAIL,
  ROUTE_GREMIEN,
  ROUTE_GREMIUM_DETAIL,
  ROUTE_PERSON_DETAIL,
  ROUTE_BEZIRKSVERTRETUNGEN,
  ROUTE_AUSSCHUESSE,
  ROUTE_DEZERNATE,
  ROUTE_BEZIRKSVERTRETUNG,
  ROUTE_BEZIRK,
  ROUTE_VEEDEL,
  ROUTE_QUELLEN,
  ROUTE_SITEMAP,
];
const MEMBERS_MENU_ROUTES = [
  ROUTE_MEMBERS_DASHBOARD,
  ROUTE_MEMBERS_PROFILE,
  ROUTE_MEMBERS_MERKLISTE,
  ROUTE_MEMBERS_MERKLISTE_DETAIL,
  ROUTE_MEMBERS_SYNC,
];

window.addEventListener("lit-localize-status", (event) => {
  const detail = (event as CustomEvent).detail as { status: string };
  if (detail.status !== "ready") return;

  for (const page of navigationConfig.pages) {
    // Ohne Scroll-Refs genügt ein Neuzuweisen der Listen.
    if (HAUPT_MENU_ROUTES.includes(page.name)) {
      page.menu = hauptMenuItems();
    } else if (MEMBERS_MENU_ROUTES.includes(page.name)) {
      page.menu = membersMenuItems();
    }
  }
});
